"use client";

/**
 * TrxCardFields — the shared TRX PayPage card input (card-number /
 * expiration-date / cvv per-field iframes) for public / customer surfaces.
 *
 * Contract mirrors `TrxAchFields` exactly:
 *
 *   - The SESSION is a prop. The parent mints it against whatever endpoint
 *     its surface authorizes — this package never owns a fetch client.
 *   - The script URL is a prop (`scriptUrl`), so Vite and Next apps each
 *     resolve their own env var.
 *   - Submission is imperative: the parent calls
 *     `ref.current.extractEncryptedData()` at the submit moment. The call
 *     deactivates the fields per §11.6 — call `enableFields()` to retry
 *     after a failure.
 *
 * PCI scope: the card number, expiry, and CVC never reach our origin.
 * The JS library encrypts them inside cross-origin iframes and returns an
 * opaque blob. The parent sends `{ data, keyId }` to the server; the server
 * passes those to TRX StorageSafe (tokenize) or directly to a charge.
 *
 * Why card-number MUST be the first mounted container (§11.5): the TRX
 * library injects a hidden orchestrator iframe into the FIRST container it
 * initializes. Reordering the cells breaks session negotiation.
 *
 * ── Apple Pay / Google Pay (opt-in, `enableWallets`) ───────────────────────
 * The library mounts its own native wallet buttons into containers we own, so
 * a surface that cannot reach our OWN wallet sheet — a guest paying at the
 * table holds a check token and nothing else — still gets both. Three rules,
 * each of which has broken production:
 *
 *   1. THE SESSION MUST DECLARE THE `Wallet` RAIL. This component takes the
 *      session as a prop and cannot mint one, so whoever mints it resolves the
 *      rail through `resolvePayPageRails` (`@order-up/common`). A session
 *      minted as `['Card']` renders no wallet button no matter what is passed
 *      here.
 *   2. A WALLET BUTTON FAILING TO MOUNT IS NEVER FATAL TO THE CARD FORM.
 *      `partitionTrxInitErrors` splits the wallet-only errors off; only the
 *      rest reach `onValidationError`. Apple Pay is offered ONLY where the
 *      browser can present it (`isApplePayAvailable` — Safari), because
 *      asking for it anywhere else provokes exactly that error.
 *   3. THE CRYPTOGRAM EXPIRES IN ~1 MINUTE. `onWalletResult` fires at the
 *      moment the sheet completes and the parent must submit immediately —
 *      never behind a second confirm.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  TrxCardBrandMark,
  normalizeCardBrand,
  type CardBrand,
} from "./TrxCardBrandMark";
import type {
  TrxAchSessionData,
  TrxPayPageCardEncryptedResult,
  TrxPayPageInitOptions,
  TrxPayPageInstance,
  TrxPayPageWalletResult,
} from "./trxPayPageTypes";
import { TRX_FIELD_CSS_BASE, TRX_FIELD_CSS_NUMBER } from "./trxFieldStyles";
import {
  describeTrxScriptFailure,
  useTrxPayPageScript,
} from "./useTrxPayPageScript";
import {
  CARD_FIELDS_NOT_READY,
  PAYMENT_FORM_UNEXPECTED_ERROR,
  buildCardEntryError,
  buildTrxCardExtractError,
  describeTrxCardError,
  describeTrxInitRejection,
} from "./trxFieldsError";
import { isApplePayAvailable, partitionTrxInitErrors } from "./trxWalletSupport";
import {
  buildTrxFieldsDiagnostic,
  isPayerInputOnly,
  type TrxFieldsDiagnosticHandler,
} from "./trxFieldsDiagnostics";
import { useTrxFieldsDiagnosticReporter } from "./TrxFieldsDiagnosticsProvider";

const COMPONENT = "TrxCardFields";

// ─────────────────────────── public API ────────────────────────────────

/** Encrypted-card payload surfaced to the parent via the imperative ref. */
export interface TrxCardEncryptedPayload {
  readonly data: string;
  readonly keyId: string;
  /** BIN (first 4–6 digits) when the JS library was initialized with `includeBIN: true`. */
  readonly bin?: string;
}

/**
 * What an Apple/Google Pay sheet produced, in the shape the charge wire takes.
 *
 * Deliberately the SAME field names as `CheckoutWalletPaymentSchema`
 * (`@order-up/schemas`) — `{ walletName, data, keyId?, bin?, cardSecurity? }` —
 * so a parent forwards it rather than translating it. A translation layer here
 * is a second place for the cryptogram to be dropped, and a dropped cryptogram
 * is a wallet sale that authorises at the plain-card interchange tier with
 * nothing red.
 */
export interface TrxCardWalletResult {
  readonly walletName: "ApplePay" | "GooglePay";
  /** The library's encrypted wallet blob. */
  readonly data: string;
  /** Rotating-key id for the blob — the JS-library path always carries one. */
  readonly keyId?: string;
  readonly bin?: string;
  /** Visa CAVV / Mastercard DSRP. ~1-MINUTE TTL — submit immediately. */
  readonly cardSecurity?: { cavv?: string; dsrpCryptogram?: string };
}

/** Imperative methods the parent calls via ref. */
export interface TrxCardFieldsHandle {
  /**
   * Extract the encrypted card payload. Throws on: not initialized, library-
   * side validation failure, or empty Encrypted block. Per §11.6 the extract
   * call deactivates fields regardless of outcome — call `enableFields()` to
   * re-activate after a failure.
   */
  extractEncryptedData(): Promise<TrxCardEncryptedPayload>;
  /** Re-activate fields after a failed `extractEncryptedData()` call. */
  enableFields(): void;
}

export interface TrxCardFieldsProps {
  /**
   * Server-minted PayPage session (clientId / payPageId / sessionId).
   * `null` while the parent is still minting — the fields shimmer.
   */
  readonly session: TrxAchSessionData | null;
  /**
   * `paypage-fields` bundle URL. Defaults to UAT — production surfaces
   * MUST pass the `.com` host from their own env.
   */
  readonly scriptUrl?: string;
  /**
   * Fires whenever readiness flips: `true` once script + session + field
   * iframes are all initialized. Parents gate their submit button on this.
   */
  readonly onReady?: (ready: boolean) => void;
  /** Library-init errors surfaced for parent display. */
  readonly onValidationError?: (message: string) => void;
  /**
   * Ask the library to return `Encrypted.BIN` (the first 4–6 digits) alongside
   * the encrypted blob — surfaced as {@link TrxCardEncryptedPayload.bin}.
   * Without it the field is ALWAYS undefined. Safe under PCI: a BIN is not
   * sensitive authentication data, and on a hosted-fields flow it is the only
   * issuer signal available to our origin.
   *
   * Opt-in rather than always-on: it changes what the vendor library returns,
   * and the surfaces that don't consume a BIN should not ask for one.
   */
  readonly includeBIN?: boolean;
  /**
   * Fires on every card-number change with the library's detected brand label
   * (`"visa"`, `"mastercard"`, `"amex"`, `"discover"`, …).
   *
   * PURELY ADDITIVE — this component always registers its own listener to
   * drive the on-field brand mark, so omitting this prop still gives the
   * cardholder brand feedback. Pass it only when the PARENT needs the value
   * too. Its identity may change freely between renders (the latest one is
   * always called); it is never an init input, so it cannot tear down the
   * field iframes.
   */
  readonly onCardTypeChange?: (cardType: string) => void;
  /**
   * Mount TRX's own Apple Pay / Google Pay buttons above the card slab.
   *
   * OFF by default, and the default is the whole compatibility story: with it
   * absent the `fields` map, the init options and the rendered DOM are
   * byte-identical to what every existing caller already gets.
   *
   * It is an ASK, not a promise. The session must have been minted declaring
   * the `Wallet` rail (`resolvePayPageRails`) or the library mounts nothing,
   * and Apple Pay additionally needs a browser that can present it. Neither
   * failure may touch the card form — see the module header.
   */
  readonly enableWallets?: boolean;
  /**
   * Fires the moment a wallet sheet completes successfully. The parent must
   * submit the charge IMMEDIATELY: the cryptogram expires in about a minute,
   * so staging this behind another confirmation step produces a payload the
   * gateway will refuse.
   *
   * A FAILED sheet goes to `onValidationError`; a CANCELLED one is silent —
   * the customer dismissed it, which is not an error to report.
   */
  readonly onWalletResult?: (result: TrxCardWalletResult) => void;
  /**
   * Receives the structured reason whenever the fields fail to initialize or
   * TRX refuses to tokenize for a reason other than the cardholder's input.
   * Always logged to the console as well. Defaults to the nearest
   * `TrxFieldsDiagnosticsProvider`.
   */
  readonly onDiagnostic?: TrxFieldsDiagnosticHandler;
}

// ─────────────────────────── iframe interior styling ───────────────────
//
// Borderless, transparent inputs that read as seamless cells of the host
// slab. Both stylesheets come from the ONE canonical module every TRX card
// surface shares — see `trxFieldStyles.ts` for why they must not be
// re-declared locally. `TRX_FIELD_CSS_NUMBER` additionally suppresses the
// library's own brand artwork, which otherwise tiles across the field and
// buries the digits; this component renders `<TrxCardBrandMark>` in host DOM
// instead (the two are halves of one pattern).

// ─────────────────────────── host-DOM styling (injected once) ───────────
//
// Slab structural CSS under its own `op-trxcard-*` namespace — coexists
// with `op-trxach-*` if both components are mounted on the same page.

const STYLE_ID = "op-trxcard-styles";
const STYLES = `
@keyframes op-trxcard-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
@keyframes op-trxcard-shimmer{0%{background-position:180% 0}100%{background-position:-180% 0}}
@keyframes op-trxcard-pop{0%{opacity:0;transform:scale(.6) translateY(-50%)}60%{opacity:1;transform:scale(1.08) translateY(-50%)}100%{transform:scale(1) translateY(-50%)}}

.op-trxcard{--op-trxcard-accent:var(--brand-primary,#18181B)}
.op-trxcard-slab{
  position:relative;border:1px solid #E7E8EC;border-radius:18px;
  background:
    radial-gradient(130% 120% at 0% -10%,rgba(255,255,255,.9),transparent 45%),
    linear-gradient(180deg,#FFFFFF 0%,#FBFBFD 100%);
  box-shadow:0 1px 2px rgba(15,18,34,.04),0 10px 30px -16px rgba(15,18,34,.12);
  transition:box-shadow .28s cubic-bezier(.2,.7,.2,1),border-color .2s ease;
}
.op-trxcard-slab:focus-within{
  border-color:color-mix(in srgb,var(--op-trxcard-accent) 75%,#E7E8EC);
  box-shadow:
    0 1px 2px rgba(15,18,34,.04),
    0 18px 40px -20px color-mix(in srgb,var(--op-trxcard-accent) 55%,transparent),
    0 0 0 4px color-mix(in srgb,var(--op-trxcard-accent) 14%,transparent);
}
.op-trxcard-row{position:relative;padding:13px 18px}
.op-trxcard-row+.op-trxcard-row{border-top:1px solid #EFF0F3}
.op-trxcard-split{display:grid;grid-template-columns:1fr 1px 1fr}
.op-trxcard-col{padding:13px 18px}
.op-trxcard-vdiv{background:#EFF0F3;width:1px;margin:8px 0}
.op-trxcard-label{display:block;font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;font-weight:700;color:#8B909B;margin-bottom:5px}
.op-trxcard-cell{height:26px}
.op-trxcard-cellwrap{position:relative;height:26px}
/* Reserve the gutter the brand mark occupies so a long PAN never runs
   underneath it. Number row only — expiry/CVC have no mark. */
.op-trxcard-cellwrap--number{margin-right:48px}
.op-trxcard-brand{position:absolute;right:18px;top:calc(50% + 7px);transform:translateY(-50%);height:26px;display:flex;align-items:center;pointer-events:none}
.op-trxcard-chip{animation:op-trxcard-pop .3s cubic-bezier(.2,.8,.2,1) both;display:block}
.op-trxcard-rowin{animation:op-trxcard-rise .55s cubic-bezier(.2,.7,.2,1) both}
.op-trxcard-shim{border-radius:7px;background:linear-gradient(90deg,#EEF0F3 8%,#F7F8FA 28%,#EEF0F3 48%);background-size:240% 100%;animation:op-trxcard-shimmer 1.35s linear infinite}
.op-trxcard-cellskel{position:absolute;inset:0;border-radius:7px;pointer-events:none}
/* Wallet buttons sit ABOVE the slab; the library owns the contents of each
   container, so all we style is the stack and the divider under it. */
.op-trxcard-wallets{margin-bottom:16px}
.op-trxcard-walletstack{display:flex;flex-direction:column;gap:8px}
.op-trxcard-or{display:flex;align-items:center;gap:12px;margin:16px 0;font-size:11px;font-weight:500;letter-spacing:.08em;text-transform:uppercase;color:#8B909B}
.op-trxcard-orline{height:1px;flex:1;background:#E7E8EC}
@media (prefers-reduced-motion:reduce){
  .op-trxcard-rowin,.op-trxcard-shim,.op-trxcard-chip{animation:none!important}
}
`.trim();

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

// ─────────────────────────── error mapping ──────────────────────────────
//
// Both the copy and the CODE live in `./trxFieldsError`. The code is
// load-bearing: without it `translateError` discards the message and the
// operator sees the generic fallback for a mistyped card number.

// ─────────────────────────── component ─────────────────────────────────

export const TrxCardFields = forwardRef<TrxCardFieldsHandle, TrxCardFieldsProps>(
  function TrxCardFields(
    {
      session,
      scriptUrl,
      onReady,
      onValidationError,
      includeBIN,
      onCardTypeChange,
      enableWallets = false,
      onWalletResult,
      onDiagnostic,
    },
    handleRef,
  ) {
    const reportDiagnostic = useTrxFieldsDiagnosticReporter(onDiagnostic);
    const {
      ready: scriptReady,
      error: scriptError,
      PayPageJS,
      retry: retryScript,
    } = useTrxPayPageScript(scriptUrl);

    // A bundle that never loaded is reported once per failure, with the
    // reason (timeout / network / no-global), like every other TRX failure.
    useEffect(() => {
      if (!scriptError) return;
      reportDiagnostic(
        buildTrxFieldsDiagnostic({
          component: COMPONENT,
          phase: "script",
          kind: "script-load-failed",
          cause: scriptError,
        }),
      );
    }, [scriptError, reportDiagnostic]);

    useEffect(() => {
      ensureStyles();
    }, []);

    // Unique container ids per instance — multiple mounts must not clash.
    const idPrefix = useId().replace(/[:]/g, "");
    const cardNumberId = `${idPrefix}-trxcard-number`;
    const expirationId = `${idPrefix}-trxcard-exp`;
    const cvvId = `${idPrefix}-trxcard-cvv`;
    const applePayId = `${idPrefix}-trxcard-apple-pay`;
    const googlePayId = `${idPrefix}-trxcard-google-pay`;

    // Apple Pay on the web is Safari-ONLY. Probed once per mount (capability
    // cannot change mid-session); asking the library for the button anywhere
    // else makes it report "Apple Pay is not supported on this browser" into
    // `instance.errors`, which is the error that took the card form down for
    // every non-Safari customer in 2026-07. Google Pay is cross-browser and
    // rides `enableWallets` alone.
    const applePayAvailable = enableWallets && isApplePayAvailable();

    const instanceRef = useRef<TrxPayPageInstance | null>(null);
    const [instanceReady, setInstanceReady] = useState(false);

    // Stable ref for the parent's callback.
    const onValidationErrorRef = useRef(onValidationError);
    useEffect(() => {
      onValidationErrorRef.current = onValidationError;
    }, [onValidationError]);

    // Same treatment for the brand-change callback: the library takes it ONCE
    // at init, so a parent passing an inline arrow must not tear the field
    // iframes down on every render.
    const onCardTypeChangeRef = useRef(onCardTypeChange);
    useEffect(() => {
      onCardTypeChangeRef.current = onCardTypeChange;
    }, [onCardTypeChange]);

    // Same treatment for the wallet-result callback, and for the same reason
    // the library takes it ONCE at init: a parent passing an inline arrow must
    // not tear the field iframes down and re-negotiate the session on every
    // render.
    const onWalletResultRef = useRef(onWalletResult);
    useEffect(() => {
      onWalletResultRef.current = onWalletResult;
    }, [onWalletResult]);

    // The detected network, for the host-DOM mark. We ALWAYS register a
    // callback with the library — it is what makes it emit the signal at all,
    // and this component is now itself a consumer, so registration can no
    // longer be conditional on the parent supplying a handler. Parents that
    // pass one still receive every value.
    //
    // Brand changes are pure host-DOM state: they are deliberately absent from
    // the init effect's deps below, so typing never re-initializes the frames.
    const [brand, setBrand] = useState<CardBrand>("generic");

    // A session is its three ids, not the object carrying them. `initialize`
    // is not idempotent, and a parent that builds `session` inline hands over a
    // new object on every render: keyed on the object, each `onReady` re-render
    // tore the iframes down and restarted them, forever.
    const clientId = session?.clientId;
    const payPageId = session?.payPageId;
    const sessionId = session?.sessionId;

    // ── Initialize the JS library once script + session are ready ───
    useEffect(() => {
      if (!scriptReady || !PayPageJS || !clientId || !payPageId || !sessionId) return;

      let cancelled = false;
      let local: TrxPayPageInstance | null = null;

      // The sheet's outcome, per wallet. SUCCESS surfaces the encrypted blob
      // and its cryptogram; FAILED is a message for the parent to show;
      // CANCELLED is the customer closing the sheet and is deliberately
      // SILENT — reporting it tells a guest their payment broke when they
      // simply changed their mind.
      const makeWalletHandler =
        (walletName: "ApplePay" | "GooglePay") =>
        (result: TrxPayPageWalletResult) => {
          if (result.status === "SUCCESS" && result.data) {
            const cs = result.data.cardSecurity;
            onWalletResultRef.current?.({
              walletName,
              data: result.data.data,
              ...(result.data.keyId ? { keyId: result.data.keyId } : {}),
              ...(result.data.bin ? { bin: result.data.bin } : {}),
              ...(cs
                ? {
                    cardSecurity: {
                      ...(cs.cavv ? { cavv: cs.cavv } : {}),
                      ...(cs.dsrpCryptogram
                        ? { dsrpCryptogram: cs.dsrpCryptogram }
                        : {}),
                    },
                  }
                : {}),
            });
          } else if (result.status === "FAILED") {
            onValidationErrorRef.current?.(
              result.message ??
                "That wallet payment could not be completed. Please try a card instead.",
            );
          }
        };

      // card-number MUST be the first field listed — the library injects its
      // hidden orchestrator iframe into the first container (§11.5). The
      // wallet entries are appended, and are ABSENT entirely when nobody asked
      // for them, so a caller that does not use wallets hands the library the
      // same map it always did.
      const fields: TrxPayPageInitOptions["fields"] = {
        "card-number": {
          containerId: cardNumberId,
          stylesCSS: TRX_FIELD_CSS_NUMBER,
        },
        "expiration-date": {
          containerId: expirationId,
          stylesCSS: TRX_FIELD_CSS_BASE,
        },
        cvv: {
          containerId: cvvId,
          stylesCSS: TRX_FIELD_CSS_BASE,
        },
        ...(applePayAvailable
          ? {
              "apple-pay": {
                containerId: applePayId,
                onPaymentResult: makeWalletHandler("ApplePay"),
                buttonConfig: { buttonType: "check-out" },
              },
            }
          : {}),
        ...(enableWallets
          ? {
              "google-pay": {
                containerId: googlePayId,
                onPaymentResult: makeWalletHandler("GooglePay"),
                buttonConfig: { buttonType: "checkout" },
              },
            }
          : {}),
      };

      PayPageJS.initialize({
        sessionData: { clientId, payPageId, sessionId },
        // Opt-in: omitted entirely unless the parent asked, so the options
        // object is byte-identical to before for callers that don't use it.
        ...(includeBIN !== undefined ? { includeBIN } : {}),
        onCardTypeChange: (cardType: string) => {
          setBrand(normalizeCardBrand(cardType));
          onCardTypeChangeRef.current?.(cardType);
        },
        fields,
      })
        .then(
          (instance) => {
            if (cancelled) {
              instance.destroy();
              return;
            }
            instanceRef.current = instance;
            local = instance;
            setInstanceReady(true);

            if (instance.errors && instance.errors.length > 0) {
              // Every init error is recorded — a wallet button that failed to
              // mount is still worth knowing about — before the copy below
              // reduces them to one sentence.
              reportDiagnostic(
                buildTrxFieldsDiagnostic({
                  component: COMPONENT,
                  phase: "initialize",
                  kind: "trx-errors",
                  errors: instance.errors,
                }),
              );
              // An OPTIONAL wallet button failing to mount must NEVER reach the
              // REQUIRED card form. Only the rest are the card form's problem.
              const { blocking } = partitionTrxInitErrors(instance.errors);
              if (blocking.length > 0) {
                onValidationErrorRef.current?.(describeTrxCardError(blocking));
              }
            }
          },
          // A REJECTED init: something threw inside TRX's library. Its message
          // is the only record of why, so it is logged and shown.
          (cause: unknown) => {
            if (cancelled) return;
            reportDiagnostic(
              buildTrxFieldsDiagnostic({
                component: COMPONENT,
                phase: "initialize",
                kind: "init-rejected",
                cause,
              }),
            );
            onValidationErrorRef.current?.(describeTrxInitRejection(cause));
          },
        )
        // Only an exception from OUR fulfilment handler reaches here — a bug of
        // ours after TRX came up cleanly, which must not read as a TRX load
        // failure.
        .catch((cause: unknown) => {
          if (cancelled) return;
          reportDiagnostic(
            buildTrxFieldsDiagnostic({
              component: COMPONENT,
              phase: "initialize",
              kind: "callback-threw",
              cause,
            }),
          );
          onValidationErrorRef.current?.(PAYMENT_FORM_UNEXPECTED_ERROR);
        });

      return () => {
        cancelled = true;
        if (local) {
          try {
            local.destroy();
          } catch {
            // Swallow destroy errors — unmount teardown only.
          }
          instanceRef.current = null;
          setInstanceReady(false);
        }
      };
    }, [
      scriptReady,
      PayPageJS,
      clientId,
      payPageId,
      sessionId,
      cardNumberId,
      expirationId,
      cvvId,
      includeBIN,
      // Both select which `fields` the library is handed, so flipping either
      // must re-initialize — otherwise the buttons asked for are never mounted
      // and nothing says so.
      enableWallets,
      applePayAvailable,
      applePayId,
      googlePayId,
      reportDiagnostic,
    ]);

    // ── Imperative handle ────────────────────────────────────────────
    const extractEncryptedData =
      useCallback(async (): Promise<TrxCardEncryptedPayload> => {
        const instance = instanceRef.current;
        if (!instance) {
          throw buildCardEntryError(
            CARD_FIELDS_NOT_READY,
            "Card form is not ready yet.",
          );
        }
        const result: TrxPayPageCardEncryptedResult =
          await instance.getEncryptedCardData();
        if (!result.isSuccess || !result.Encrypted) {
          // A cardholder typo is shown by field name and is not a defect;
          // anything else (a dead iframe, a server error) is recorded.
          if (!isPayerInputOnly(result.errors)) {
            reportDiagnostic(
              buildTrxFieldsDiagnostic({
                component: COMPONENT,
                phase: "extract",
                kind: "trx-errors",
                errors: result.errors,
              }),
            );
          }
          throw buildTrxCardExtractError(result.errors);
        }
        return {
          data: result.Encrypted.Data,
          keyId: result.Encrypted.KeyId,
          ...(result.Encrypted.BIN !== undefined
            ? { bin: result.Encrypted.BIN }
            : {}),
        };
      }, [reportDiagnostic]);

    const enableFields = useCallback(() => {
      instanceRef.current?.enableCardDataFields();
    }, []);

    useImperativeHandle(
      handleRef,
      () => ({ extractEncryptedData, enableFields }),
      [extractEncryptedData, enableFields],
    );

    // ── Readiness signal ────────────────────────────────────────────
    const isLoading = !scriptReady || !session || !instanceReady;

    useEffect(() => {
      onReady?.(!isLoading);
    }, [isLoading, onReady]);

    const showSkeleton = isLoading && !scriptError;

    // ── Render ──────────────────────────────────────────────────────
    return (
      <div className="op-trxcard">
        {/* Apple Pay / Google Pay, ABOVE the card form — wallet-first, per
            Google's own button guidance. The library mounts its native button
            into each container and clears whatever was there, so these stay
            empty divs. The Apple Pay slot renders only where the library will
            actually mount it; an empty slot plus a spurious "unsupported"
            init error is the alternative. */}
        {enableWallets && (
          <div className="op-trxcard-wallets" data-testid="trxcard-wallets">
            <div className="op-trxcard-walletstack">
              {applePayAvailable && <div id={applePayId} />}
              <div id={googlePayId} />
            </div>
            <div className="op-trxcard-or">
              <span className="op-trxcard-orline" />
              or pay with card
              <span className="op-trxcard-orline" />
            </div>
          </div>
        )}

        {/* The slab. card-number MUST be first (§11.5 orchestrator). */}
        <div className="op-trxcard-slab">
          {/* Row 1: card number */}
          <div
            className="op-trxcard-row op-trxcard-rowin"
            style={{ animationDelay: "40ms" }}
          >
            <label className="op-trxcard-label" htmlFor={cardNumberId}>
              Card number
            </label>
            <div className="op-trxcard-cellwrap op-trxcard-cellwrap--number">
              <div id={cardNumberId} className="op-trxcard-cell" />
              {showSkeleton && (
                <span className="op-trxcard-cellskel op-trxcard-shim" />
              )}
            </div>
            {/* Our own mark, at the field's right edge. The library's is
                suppressed inside the iframe (TRX_FIELD_CSS_NUMBER) because it
                paints tiled across the input. Keyed on the brand so each
                swap re-runs the pop-in. */}
            {!showSkeleton && (
              <div className="op-trxcard-brand">
                <TrxCardBrandMark
                  key={brand}
                  brand={brand}
                  className="op-trxcard-chip"
                />
              </div>
            )}
          </div>

          {/* Row 2: expiry + CVC split */}
          <div
            className="op-trxcard-split op-trxcard-rowin"
            style={{ animationDelay: "110ms" }}
          >
            <div className="op-trxcard-col">
              <label className="op-trxcard-label" htmlFor={expirationId}>
                Expiry
              </label>
              <div className="op-trxcard-cellwrap">
                <div id={expirationId} className="op-trxcard-cell" />
                {showSkeleton && (
                  <span className="op-trxcard-cellskel op-trxcard-shim" />
                )}
              </div>
            </div>
            <div className="op-trxcard-vdiv" />
            <div className="op-trxcard-col">
              <label className="op-trxcard-label" htmlFor={cvvId}>
                CVC
              </label>
              <div className="op-trxcard-cellwrap">
                <div id={cvvId} className="op-trxcard-cell" />
                {showSkeleton && (
                  <span className="op-trxcard-cellskel op-trxcard-shim" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fatal-error display — script failed to load. */}
        {scriptError && (
          <div className="mt-3 space-y-2" role="alert">
            <p className="text-sm font-medium text-destructive">
              {describeTrxScriptFailure(scriptError)}
            </p>
            <button
              type="button"
              onClick={retryScript}
              className="rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    );
  },
);
